import DashboardLayout from "@/layout/dashboard-layout";
import { ReactNode, useState } from "react";
import { router } from '@inertiajs/react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileText, Search, Plus, Eye, Calendar, Copy, ExternalLink } from "lucide-react";
import { toast } from 'sonner';

type Post = {
  id: number;
  title: string;
  blog: string;
  status: string;
  views: number;
  created_at: string;
  scheduled_at?: string;
  category: string;
  author: string;
  content_preview: string;
  url?: string;
  published_in?: string;
};

type PostsIndexProps = {
  posts: Post[];
  filters: {
    status: string[];
    blogs: string[];
    categories: string[];
  };
};

const PostsIndex = (props: PostsIndexProps) => {
  const { posts, filters } = props;
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [blogFilter, setBlogFilter] = useState("all");

  // Função para copiar URL do post
  const copyPostUrl = async (url?: string) => {
    const urlToCopy = url || `https://blog-exemplo.com/posts/demo-${Math.random().toString(36).substr(2, 9)}`;
    
    if (!url) {
      toast.warning("URL não disponível - usando URL de exemplo para demonstração");
    }
    
    try {
      await navigator.clipboard.writeText(urlToCopy);
      toast.success(`✅ URL copiada: ${urlToCopy}`, {
        duration: 3000,
        description: "A URL foi copiada para a área de transferência!"
      });
      console.log('✅ URL copiada com sucesso');
    } catch (error) {
      console.log('❌ Erro no clipboard API, tentando fallback:', error);
      // Fallback para navegadores que não suportam clipboard API
      try {
        const textArea = document.createElement('textarea');
        textArea.value = urlToCopy;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        toast.success(`✅ URL copiada: ${urlToCopy}`, {
          duration: 3000,
          description: "A URL foi copiada para a área de transferência!"
        });
        console.log('✅ URL copiada com fallback');
      } catch (fallbackError) {
        console.log('❌ Erro no fallback também:', fallbackError);
        toast.error("Erro ao copiar URL - tente novamente");
      }
    }
  };

  // Função para abrir post em nova aba
  const openPost = (url?: string) => {
    
    // Teste simples primeiro
    
    // Se não há URL, usar URL de exemplo para demonstração
    const urlToOpen = url || `https://blog-exemplo.com/posts/demo-${Math.random().toString(36).substr(2, 9)}`;
    
    if (!url) {
      toast.info("URL não disponível - abrindo página de exemplo para demonstração");
    } else {
      toast.success("🚀 Abrindo post em nova aba...");
    }
    
    try {
      window.open(urlToOpen, '_blank', 'noopener,noreferrer');
    } catch (error) {
      toast.error('Erro ao abrir post em nova aba');
    }
  };

  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.blog.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || post.status === statusFilter;
    const matchesBlog = blogFilter === "all" || post.blog === blogFilter;
    
    return matchesSearch && matchesStatus && matchesBlog;
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "published":
        return <Badge variant="default">Publicado</Badge>;
      case "draft":
        return <Badge variant="secondary">Rascunho</Badge>;
      case "scheduled":
        return <Badge variant="outline">Agendado</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Gerenciar Posts</h1>
          <p className="text-muted-foreground">
            Visualize e gerencie todas as postagens da sua rede
          </p>
        </div>
        <Button onClick={() => router.visit('/dashboard/posts/new')}>
          <Plus className="mr-2 h-4 w-4" />
          Nova Postagem
        </Button>
      </div>

      {/* Estatísticas */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Posts</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{posts.length}</div>
            <p className="text-xs text-muted-foreground">Todos os blogs</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Publicados</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {posts.filter(p => p.status === "published").length}
            </div>
            <p className="text-xs text-muted-foreground">Posts ativos</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Rascunhos</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {posts.filter(p => p.status === "draft").length}
            </div>
            <p className="text-xs text-muted-foreground">Aguardando publicação</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Visualizações Totais</CardTitle>
            <Eye className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {posts.reduce((sum, post) => sum + post.views, 0).toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">Todos os posts</p>
          </CardContent>
        </Card>
      </div>

      {/* Filtros e Busca */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Posts</CardTitle>
          <CardDescription>
            Visualize e gerencie todas as postagens
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar posts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os Status</SelectItem>
                {filters.status.slice(1).map((status) => (
                  <SelectItem key={status} value={status}>
                    {status === "published" ? "Publicado" : 
                     status === "draft" ? "Rascunho" : 
                     status === "scheduled" ? "Agendado" : status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={blogFilter} onValueChange={setBlogFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Blog" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os Blogs</SelectItem>
                {filters.blogs.map((blog) => (
                  <SelectItem key={blog} value={blog}>{blog}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Título</TableHead>
                <TableHead>Blog</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Categoria</TableHead>
                <TableHead>Visualizações</TableHead>
                <TableHead>Data Criação</TableHead>
                <TableHead>Publicado em</TableHead>
                <TableHead>Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPosts.map((post) => (
                <TableRow key={post.id}>
                  <TableCell className="font-medium max-w-[300px] truncate">
                    {post.title}
                  </TableCell>
                  <TableCell>{post.blog}</TableCell>
                  <TableCell>{getStatusBadge(post.status)}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{post.category}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-1">
                      <Eye className="h-3 w-3 text-muted-foreground" />
                      <span>{post.views.toLocaleString()}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-1">
                      <Calendar className="h-3 w-3 text-muted-foreground" />
                      <span className="text-sm">{post.created_at}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {post.published_in ? (
                      <div className="flex items-center space-x-1">
                        <Calendar className="h-3 w-3 text-green-500" />
                        <span className="text-sm text-green-600">{post.published_in}</span>
                      </div>
                    ) : (
                      <span className="text-sm text-muted-foreground">Não publicado</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {post.status === 'published' && post.url ? (
                      <div className="flex items-center space-x-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            copyPostUrl(post.url);
                          }}
                          className={`transition-all duration-200 hover:bg-blue-50 hover:text-blue-600 hover:scale-105 cursor-pointer`}
                          title="Copiar URL do post"
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            openPost(post.url);
                          }}
                          className={`transition-all duration-200 hover:bg-green-50 hover:text-green-600 hover:scale-105 cursor-pointer`}
                          title="Abrir post em nova aba"
                        >
                          <ExternalLink className="h-3 w-3" />
                        </Button>
                      </div>
                    ) : (
                      <span className="text-sm text-muted-foreground">-</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

PostsIndex.layout = (page: ReactNode) => (
  <DashboardLayout>{page}</DashboardLayout>
);

export default PostsIndex;
